import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type GetStagePlaybookResult } from '@whinerp/client';
import { Button, Skeleton } from '@whinerp/ui';
import { useT } from '../../app/i18n.js';

/**
 * Writing the playbook for one stage (F-056).
 *
 * Objections are a pair - what the customer says and what to say back - and
 * they are edited as a pair, because half of one is what a rep finds mid-call
 * when there is nothing underneath. The server refuses half a pair by name and
 * that refusal is drawn here rather than swallowed.
 */
export function StagePlaybookEditor({
  stageId,
  stageLabel,
}: {
  stageId: string;
  stageLabel: string;
}): JSX.Element {
  const t = useT();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<GetStagePlaybookResult | null>(null);

  const playbook = useQuery<GetStagePlaybookResult, ApiError>({
    queryKey: ['stage-playbook', stageId],
    queryFn: () => api.getStagePlaybook({ path: { stageId } }),
  });

  /*
   * The draft wins over the server's copy while somebody is typing, and the
   * server's answer replaces the draft when it comes back - so what is on
   * screen after a save is what was stored, not what was typed.
   */
  const current: GetStagePlaybookResult = draft ??
    playbook.data ?? {
      stageId,
      stageLabel,
      guidance: null,
      questions: [],
      objections: [],
      exitRequires: [],
    };

  const save = useMutation<GetStagePlaybookResult, ApiError>({
    mutationFn: () =>
      api.saveStagePlaybook({
        path: { stageId },
        body: {
          questions: current.questions,
          objections: current.objections,
          // Sent together, always. The server clears the criteria when the
          // guidance arrives without them.
          guidance: current.guidance,
          exitRequires: current.exitRequires,
        },
      }),
    onSuccess: async (saved) => {
      setDraft(saved);
      await queryClient.invalidateQueries({ queryKey: ['stage-playbook', stageId] });
    },
  });

  if (playbook.isPending) return <Skeleton className="h-block-sm w-full" />;
  if (playbook.isError) {
    return (
      <p data-playbook-refusal className="text-dense text-danger-text">
        {playbook.error.message}
      </p>
    );
  }

  const edit = (next: Partial<GetStagePlaybookResult>): void =>
    setDraft({ ...current, ...next });

  return (
    <div
      data-stage-playbook-editor={stageId}
      data-playbook-saved={save.isSuccess ? 'yes' : 'no'}
      className="flex flex-col gap-2 rounded-control bg-sunken px-3 py-2"
    >
      {save.error ? (
        <p data-playbook-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">
          {save.error.message}
        </p>
      ) : null}

      {/* F-043's paragraph and criteria. Editable here for the first time:
          the operation that writes them has existed since G-081 with nothing
          calling it, so a workspace could only set them through the API. */}
      <p className="text-micro uppercase tracking-label text-ink-meta">{t('playbook.guidance')}</p>
      <textarea
        data-playbook-guidance
        aria-label={`${stageLabel}, ${t('playbook.guidance')}`}
        rows={2}
        className="w-full rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
        value={current.guidance ?? ''}
        onChange={(event) => edit({ guidance: event.target.value === '' ? null : event.target.value })}
      />

      <p className="text-micro uppercase tracking-label text-ink-meta">{t('playbook.exit')}</p>
      {current.exitRequires.map((field, index) => (
        <div key={index} className="flex items-center gap-2">
          <input
            data-playbook-exit-input={String(index)}
            aria-label={`${stageLabel}, ${t('playbook.exit')} ${index + 1}`}
            className="min-w-0 flex-1 rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
            value={field}
            onChange={(event) => {
              const exitRequires = [...current.exitRequires];
              exitRequires[index] = event.target.value;
              edit({ exitRequires });
            }}
          />
          <Button
            intent="ghost"
            size="small"
            data-playbook-exit-remove={String(index)}
            onClick={() =>
              edit({ exitRequires: current.exitRequires.filter((_, at) => at !== index) })
            }
          >
            {t('playbook.remove')}
          </Button>
        </div>
      ))}
      <div>
        <Button
          intent="ghost"
          size="small"
          data-playbook-exit-add
          onClick={() => edit({ exitRequires: [...current.exitRequires, ''] })}
        >
          {t('playbook.exit.add')}
        </Button>
      </div>

      <p className="text-micro uppercase tracking-label text-ink-meta">{t('playbook.questions')}</p>
      {current.questions.map((question, index) => (
        <div key={index} className="flex items-center gap-2">
          <input
            data-playbook-question-input={String(index)}
            aria-label={`${stageLabel}, ${t('playbook.questions')} ${index + 1}`}
            className="min-w-0 flex-1 rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
            value={question}
            onChange={(event) => {
              const questions = [...current.questions];
              questions[index] = event.target.value;
              edit({ questions });
            }}
          />
          <Button
            intent="ghost"
            size="small"
            data-playbook-question-remove={String(index)}
            onClick={() => edit({ questions: current.questions.filter((_, at) => at !== index) })}
          >
            {t('playbook.remove')}
          </Button>
        </div>
      ))}
      <div>
        <Button
          intent="ghost"
          size="small"
          data-playbook-question-add
          onClick={() => edit({ questions: [...current.questions, ''] })}
        >
          {t('playbook.question.add')}
        </Button>
      </div>

      <p className="text-micro uppercase tracking-label text-ink-meta">{t('playbook.objections')}</p>
      {current.objections.map((entry, index) => (
        <div key={index} className="flex flex-wrap items-center gap-2">
          <input
            data-playbook-objection-input={String(index)}
            aria-label={`${stageLabel}, ${t('playbook.objections')} ${index + 1}`}
            className="min-w-0 flex-1 rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
            value={entry.objection}
            onChange={(event) => {
              const objections = [...current.objections];
              objections[index] = { ...entry, objection: event.target.value };
              edit({ objections });
            }}
          />
          <input
            data-playbook-response-input={String(index)}
            aria-label={`${stageLabel}, ${t('playbook.response')} ${index + 1}`}
            className="min-w-0 flex-1 rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
            value={entry.response}
            onChange={(event) => {
              const objections = [...current.objections];
              objections[index] = { ...entry, response: event.target.value };
              edit({ objections });
            }}
          />
          <Button
            intent="ghost"
            size="small"
            data-playbook-objection-remove={String(index)}
            onClick={() => edit({ objections: current.objections.filter((_, at) => at !== index) })}
          >
            {t('playbook.remove')}
          </Button>
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <Button
          intent="ghost"
          size="small"
          data-playbook-objection-add
          onClick={() => edit({ objections: [...current.objections, { objection: '', response: '' }] })}
        >
          {t('playbook.objection.add')}
        </Button>
        <Button
          intent="secondary"
          size="small"
          data-playbook-save={stageId}
          onClick={() => save.mutate()}
        >
          {t('playbook.save')}
        </Button>
      </div>
    </div>
  );
}
